import type { ApiErrorCode } from "@seeft/shared";

import { API_URL } from "../env";
import { getStoredAdminToken } from "../lib/adminToken";
import { getStoredDevToken } from "../lib/devToken";

const GENERIC_MESSAGE = "Something went wrong. Please try again later.";

const ACTIONABLE_MESSAGES: Partial<Record<ApiErrorCode, string>> = {
  SUBSCRIPTION_REQUIRED: "Subscribe to keep searching.",
  YOUTUBE_KEY_REQUIRED: "Connect your YouTube API key to search.",
  YOUTUBE_KEY_INVALID:
    "Your YouTube API key is invalid or revoked. Please reconnect.",
  RATE_LIMITED: "Too many requests. Please wait a moment.",
  QUOTA_EXCEEDED: "You've reached your daily search limit.",
  YOUTUBE_KEY_QUOTA_EXHAUSTED:
    "Your YouTube API key has hit its daily quota.",
  YOUTUBE_API_NOT_ENABLED:
    "YouTube Data API v3 is not enabled on your GCP project.",
  YOUTUBE_KEY_VALIDATION_FAILED:
    "Could not verify your key. Please try again."
};

export type ApiError = {
  code: string;
  message: string;
  status: number;
};

export type ApiResult<T> =
  | { data: T; error: null }
  | { data: null; error: ApiError };

type AuthMode = "user" | "admin";
type AdminRequestOptions = {
  allowDevToken?: boolean;
};

function classifyError(code: string, backendMessage: string, status: number): ApiError {
  if (code === "VALIDATION_ERROR") {
    return { code, message: backendMessage, status };
  }

  return {
    code,
    message: ACTIONABLE_MESSAGES[code as ApiErrorCode] ?? GENERIC_MESSAGE,
    status
  };
}

async function request<T>(
  path: string,
  init: RequestInit,
  mode: AuthMode = "user",
  _isRetry = false,
  adminOptions?: AdminRequestOptions
): Promise<ApiResult<T>> {
  if (mode === "admin") {
    const adminToken = getStoredAdminToken();
    const devToken = getStoredDevToken();

    if (!adminToken && !(adminOptions?.allowDevToken && devToken)) {
      return {
        data: null,
        error: {
          code: "FORBIDDEN",
          message: adminOptions?.allowDevToken
            ? "Admin or dev token required"
            : "Admin token required",
          status: 403
        }
      };
    }

    const headers = new Headers(init.headers);
    if (adminToken) {
      headers.set("X-Admin-Token", adminToken);
    } else if (devToken) {
      headers.set("X-Dev-Token", devToken);
    }

    let response: Response;
    try {
      response = await fetch(`${API_URL}${path}`, {
        ...init,
        headers,
        credentials: "include"
      });
    } catch {
      return {
        data: null,
        error: { code: "NETWORK_ERROR", message: "Could not reach the admin API.", status: 0 }
      };
    }

    if (response.status === 204) {
      return { data: null as T, error: null };
    }

    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      if (!response.ok) {
        return {
          data: null,
          error: {
            code: "PARSE_ERROR",
            message: "Admin API returned an unreadable response.",
            status: response.status
          }
        };
      }
    }

    if (!response.ok) {
      const payload = body as {
        error?: {
          code?: string;
          message?: string;
        };
      };

      return {
        data: null,
        error: {
          code: payload.error?.code ?? "UNKNOWN",
          message: payload.error?.message ?? "Admin request failed.",
          status: response.status
        }
      };
    }

    return { data: body as T, error: null };
  }

  const headers = new Headers(init.headers);

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...init,
      headers,
      credentials: "include"
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return {
        data: null,
        error: { code: "ABORTED", message: "Request cancelled", status: 0 }
      };
    }

    return {
      data: null,
      error: { code: "NETWORK_ERROR", message: GENERIC_MESSAGE, status: 0 }
    };
  }

  if (response.status === 204) {
    return { data: null as T, error: null };
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return {
      data: null,
      error: {
        code: "PARSE_ERROR",
        message: GENERIC_MESSAGE,
        status: response.status
      }
    };
  }

  if (!response.ok) {
    const payload = body as {
      error?: {
        code?: string;
        message?: string;
      };
    };

    return {
      data: null,
      error: classifyError(
        payload.error?.code ?? "UNKNOWN",
        payload.error?.message ?? "Unknown error",
        response.status
      )
    };
  }

  return { data: body as T, error: null };
}

export function apiGet<T>(path: string): Promise<ApiResult<T>> {
  return request<T>(path, { method: "GET" });
}

export function apiPost<T>(
  path: string,
  body: unknown,
  options?: { signal?: AbortSignal; headers?: Record<string, string> }
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...options?.headers
  };
  return request<T>(path, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal: options?.signal
  });
}

export function apiPatch<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return request<T>(path, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(body)
  });
}

export function apiDelete(path: string): Promise<ApiResult<null>> {
  return request<null>(path, { method: "DELETE" });
}

export function adminGet<T>(path: string): Promise<ApiResult<T>> {
  return request<T>(path, { method: "GET" }, "admin");
}

export function adminPost<T>(
  path: string,
  body: unknown,
  options?: AdminRequestOptions
): Promise<ApiResult<T>> {
  return request<T>(
    path,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    },
    "admin",
    false,
    options
  );
}

export function adminPut<T>(
  path: string,
  body: unknown,
  options?: AdminRequestOptions
): Promise<ApiResult<T>> {
  return request<T>(
    path,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    },
    "admin",
    false,
    options
  );
}

export function adminGetWithDevToken<T>(path: string): Promise<ApiResult<T>> {
  return request<T>(path, { method: "GET" }, "admin", false, { allowDevToken: true });
}
